
import router from "@/router";
import { useUserStore } from "@/stores/modules/user";
import { getApiBaseUrl, isLocalHost } from "@/utils/env";
import axios from "axios";
const message = useMessage();

// create an axios instance
const service = axios.create({
    baseURL: getApiBaseUrl() || "/", // url = base url + request url
    withCredentials: true, // send cookies when cross-domain requests
    timeout: 180000 // request timeout
});

const errorMessage = (msg: any) => {
    message.error(msg);
};

interface JslsAuthUser {
    id?: number | string;
    userName?: string;
    companyCode?: string;
    shop_code?: string;
    userOrgCode?: string;
    newH5Token?: string;
    shop_selected?: {
        deliver_code?: string;
        store_id?: number | string;
    };
}

const toHeaderValue = (value: unknown) => {
    const text = String(value);
    const hasNonLatin1 = Array.from(text).some((char) => char.charCodeAt(0) > 255);
    return hasNonLatin1 ? encodeURIComponent(text) : text;
};

const appendJslsAuthHeaders = (headers: Record<string, unknown>) => {
    const user = useUserStore().userInfo as JslsAuthUser | undefined;
    if (!user) {
        return;
    }
    const shop = user.shop_selected;
    const entries: Array<[string, unknown]> = [
        ["auth_dc_code", shop?.deliver_code],
        ["auth_org_code", user.companyCode],
        ["auth_shop_code", user.shop_code],
        ["auth_storeid", shop?.store_id],
        ["auth_user_org_code", user.userOrgCode],
        ["auth_userid", user.id],
        ["auth_username", user.userName],
        ["newh5token", user.newH5Token]
    ];
    entries.forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") {
            return;
        }
        headers[key] = toHeaderValue(value);
    });
};

/**
 * 请求失败后的错误统一处理
 * @param {Number} status 请求失败的状态码
 *
 */
const errorHandle = (status: number, other: string, requestUrl: string) => {
    const userStore = useUserStore();
    // 状态码判断
    switch (status) {
    case 400:
        errorMessage(other);
        break;
    case 401:
        errorMessage("登录状态失效，请重新登录");
        userStore.clearUserInfo();
        localStorage.clear();
        router.push("/login");
        break;
    case 403:
        errorMessage(`没有操作权限: ${requestUrl}`);
        break;
    case 404:
        errorMessage(`找不到资源: ${requestUrl}`);
        break;
    case 502:
        errorMessage("网关错误");
        break;
    default:
        errorMessage(`服务器错误: ${requestUrl}`);
    }
};


service.interceptors.request.use(
    (config: any) => {
        // 对齐 H5：/api/ 前缀是总部商品（JSLS）域接口，与 LM 主站不同域名，
        // 需把 baseURL 改写到独立的 JSLS 网关（VITE_CONFIG_SERVER_PROXY_TARGET_JSLS）；
        // 本地开发保持相对路径，由 vite /api 代理转发，规避 localhost 跨域与 Cookie 问题
        const jslsBaseUrl = import.meta.env.VITE_CONFIG_SERVER_PROXY_TARGET_JSLS;
        if (
            typeof config.url === "string" &&
            config.url.startsWith("/api/") &&
            jslsBaseUrl &&
            !isLocalHost()
        ) {
            config.baseURL = String(jslsBaseUrl).trim().replace(/\/+$/, "");
        }

        config.headers = {
            ...config.headers,
            "X-APP": "cn.lifemg.union",
            "X-VER": "9.8.27",
            "X-BUILD": "1",
            "X-OS": "iOS",
            "X-OS1": "iOS",
            "pcToken": localStorage.getItem("pcToken") || "",
            "mgChannel": "pc",
            "shopId": localStorage.getItem("storeId") || ""
        } as any;
        appendJslsAuthHeaders(config.headers);

        if (config.formData) {
            const formData = new FormData();
            config.headers["content-type"] = "application/x-www-form-urlencoded";
            if (config.params) {
                const params = config.params;
                Object.keys(params).forEach((key) => {
                    formData.append(key, params[key]);
                });
                config.params = formData;
            } else if (config.data) {
                const datas = config.data;
                Object.keys(datas).forEach((key) => {
                    formData.append(key, datas[key]);
                });
                config.data = formData;
            }
        }
        return config;
    },
    (error) => {
        console.log(error); // for debug
        return Promise.reject(error);
    }
);

service.interceptors.response.use(
    (response) => {
        // 二进制响应（如体验版二维码图片）不走业务 code 包装
        if (response.config.responseType === "blob" || response.data instanceof Blob) {
            const contentType = String(response.headers?.["content-type"] || "");
            if (contentType.includes("application/json")) {
                return (response.data as Blob).text().then((text) => {
                    try {
                        const json = JSON.parse(text) as { code?: number; message?: string; msg?: string };
                        errorMessage(json.message ?? json.msg);
                        return Promise.reject(new Error((json.message ?? json.msg) || "Error"));
                    } catch {
                        return Promise.reject(new Error("请求失败"));
                    }
                });
            }
            return response.data;
        }

        const res = response.data;
        // 需要放出给页面自己处理的code
        const codeList = [0, 30004, 200, 5001, 98000, 200018, 200003, 103002, 160015];
        if (codeList.includes(res?.code)) {
            return res;
        } else {
            // 需要全局处理的code
            if (res?.code === 60004 && response.config.url === "/v4/miniMall/getToken") {
                errorHandle(401, res?.message, response.config.url);
            } else {
                const msg = res?.message ?? res?.msg;
                if (msg) {
                    errorMessage(msg);
                } else if (typeof res === "string" && (res.includes("<!DOCTYPE") || res.includes("<html"))) {
                    console.warn(`[API] 接口 ${response.config.url} 返回了 HTML 内容，可能为网关未路由或服务未就绪`);
                }
            }
            // 自定义code全局拦截示例
            const errMsg = (res?.message ?? res?.msg) || (typeof res === "string" ? "接口返回异常内容" : "Error");
            return Promise.reject(new Error(errMsg));
        }

    },
    (error) => {
        const { response } = error;
        if (response) {
            const data = response.data;
            if (data instanceof Blob) {
                return data.text().then((text) => {
                    try {
                        const json = JSON.parse(text) as { message?: string; msg?: string };
                        errorHandle(response.status, json.message ?? json.msg ?? "", error.config?.url);
                    } catch {
                        errorHandle(response.status, "", error.config?.url);
                    }
                    return Promise.reject(response);
                });
            }
            errorHandle(response.status, data?.message ?? data?.msg, error.config?.url);
            return Promise.reject(error.response);
        }
        if (error.config?.url === "/v4/miniMall/getToken") {
            errorMessage("获取会员商城鉴权信息失败，请稍后重试");
        }
        console.log("err" + error); // for debug

        return Promise.reject(error);
    }
);

export default service;
